import React, { useCallback, useState } from 'react';

import styled, { keyframes } from 'styled-components';

import { DocumentsSections } from '@cd3p/core/modules/documents';
import { APIAttachment } from '@cd3p/core/types/api';
import { ICommand } from '@uiw/react-md-editor';
import { useHandleApiResult } from 'hooks/useHandleApiResult';

import { useParams, useTranslation } from 'third-party';

import { useHelpCenter } from 'modules/helpCenter';

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const SpinnerWrapper = styled.div`
  width: 7px;
  height: 7px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top: 3px solid #000;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

interface MarkDownUploadFileButtonProps {
  command: ICommand;
  disabled: boolean;
  executeCommand: (command: ICommand, name?: string) => void;
  fileUrlRef: React.MutableRefObject<string>;
}

export const MarkDownUploadFileButton: React.FC<
  MarkDownUploadFileButtonProps
> = ({ command, disabled, executeCommand, fileUrlRef }) => {
  const { applicationId } = useParams();
  const { t } = useTranslation();
  const [isAttachmentUploading, setIsAttachmentUploading] = useState(false);
  const { uploadHelpCenterDocumentAttachment } = useHelpCenter(
    applicationId as DocumentsSections,
  );

  const handleApiResult = useHandleApiResult();

  const onUploadFile = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];

      if (file) {
        const formData = new FormData();
        formData.append('file', file);

        handleApiResult<APIAttachment>(
          async () => {
            setIsAttachmentUploading(true);
            return uploadHelpCenterDocumentAttachment?.(formData);
          },
          ({ showBaseToast, result }) => {
            showBaseToast(t('docs.toast.attachmentUploaded'));
            setIsAttachmentUploading(false);
            fileUrlRef.current = result.payload.downloadUrl;
            executeCommand(command, command.groupName);
          },
          () => {
            setIsAttachmentUploading(false);
          },
        );
      }
    },
    [
      uploadHelpCenterDocumentAttachment,
      handleApiResult,
      setIsAttachmentUploading,
      t,
      executeCommand,
      command,
      fileUrlRef,
    ],
  );

  return (
    <>
      <input
        type="file"
        id="file-upload"
        style={{ display: 'none' }}
        onChange={async evn => {
          await onUploadFile(evn);
        }}
      />
      <button
        aria-label={t('docs.menu.uploadAttachment')}
        title={t('docs.menu.uploadAttachment')}
        onClick={evn => {
          evn.preventDefault();
          document.getElementById('file-upload')?.click();
        }}
        disabled={disabled || isAttachmentUploading}
      >
        {isAttachmentUploading && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <SpinnerWrapper />
          </div>
        )}

        {!isAttachmentUploading && (
          <svg width="13" height="13" viewBox="0 0 20 20">
            <path
              fill="currentColor"
              d="M15 9c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm4-7H1c-.55 0-1 .45-1 1v14c0 .55.45 1 1 1h18c.55 0 1-.45 1-1V3c0-.55-.45-1-1-1zm-1 13l-6-5-2 2-4-5-4 8V4h16v11z"
            />
          </svg>
        )}
      </button>
    </>
  );
};
